import React, { forwardRef } from 'react';
import classNames from 'classnames';

import Icon from '@sv-support-sdk/ui-components/src/components/Media/Icon';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';

import Button from '../Button';
import DropdownPanel from './DropdownPanel';
import Loader from '../../Feedback/Loader';

import { useAppSelector } from '../../../store/hooks';
import { selectDropdownList, selectPagination } from '../../../store/selectors/search';
import { formResultLink } from '../../../helpers/links';
import { $l } from '../../../locales';
import { routes } from '../../../routing';
import { SEARCH_FIELD_ID } from '../../../constants/search';

import css from './style.module.scss';

interface ISearchTypeMapping {
  [key: string]: { title: string; icon: string };
}

interface IProps {
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  clearSearchValue: () => void;
  showDropdown: boolean;
  onFocus: () => void;
  isSearchInFocus: boolean;
  searchValue: string;
  loading: boolean;
  handleSearchAllResults: () => void;
  navigateToSearchResultItem: (item: string) => void;
}

const DesktopSearch = forwardRef<HTMLInputElement, IProps>(function DesktopSearch(
  {
    handleInputChange,
    handleInputKeyUp,
    clearSearchValue,
    showDropdown,
    onFocus,
    isSearchInFocus,
    searchValue,
    loading,
    handleSearchAllResults,
    navigateToSearchResultItem,
  },
  ref,
) {
  const dropdownList = useAppSelector(selectDropdownList);
  const pagination = useAppSelector(selectPagination);

  const searchTypeMapping: ISearchTypeMapping = {
    topic: { icon: '24--article', title: 'Guide' },
    faq_topic: { icon: '24--live-help', title: 'FAQ' },
    release_note: { icon: '24--new-releases', title: 'Release note' },
    ticket: { icon: '24--history', title: 'Ticket' },
  };

  const dropdownSearchResults = dropdownList.map((item) => {
    const { type } = item;
    const { title, icon } = searchTypeMapping[type];
    return { ...item, typeTitle: title, typeIcon: icon };
  });

  const clearInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    clearSearchValue();
  };

  return (
    <div className={css.search} id={SEARCH_FIELD_ID}>
      <div className={css.search__icon}>
        <Icon id="16--search" />
      </div>
      <div className={css.search__control}>
        <label>
          <input
            ref={ref}
            placeholder={$l('SEARCH.SEARCH_HELP_CENTER')}
            value={searchValue}
            onFocus={onFocus}
            onChange={handleInputChange}
            onKeyUp={handleInputKeyUp}
            className={classNames(css.search__input, {
              [css.withDropdown]: showDropdown,
            })}
          />
          <div
            className={classNames(css.search__inputIcon, {
              [css.search__inputIcon_position_left]: searchValue.trim() || isSearchInFocus,
            })}
          >
            <Icon id="24--search" />
          </div>
          {searchValue.trim() ? (
            <div className={css.search__clearButton}>
              <Button
                onMouseDown={clearInput}
                iconSize="24"
                icon="24--close"
                variant="icon"
                noHover
              />
            </div>
          ) : null}
        </label>
      </div>
      {showDropdown && (
        <div className={css.search__dropdown}>
          {!loading && dropdownSearchResults.length > 0 && (
            <>
              <div className={css.search__dropdownSection}>
                <List gap="8" width="full">
                  {dropdownSearchResults.map((result) => (
                    <DropdownPanel
                      id={result.id}
                      key={result.id || result.slug}
                      icon={result.typeIcon}
                      title={result.title || result.name || result.question}
                      subtitle={result.typeTitle}
                      onClick={() => navigateToSearchResultItem(formResultLink(result) || '')}
                      link={formResultLink(result)}
                    />
                  ))}
                </List>
              </div>
              <div className={css.search__dropdownSection}>
                <DropdownPanel
                  icon="24--search"
                  title={`All ${pagination.total} search results for “${searchValue}”`}
                  showHint
                  onClick={handleSearchAllResults}
                  link={`${routes.search}?q=${searchValue}`}
                />
              </div>
            </>
          )}
          {!loading && !dropdownSearchResults.length && (
            <div className={css.search__dropdownSection}>
              <DropdownPanel title={$l('SEARCH.NO_RESULTS')} disabled />
            </div>
          )}
          {loading && (
            <div className={css.search__dropdownSection}>
              <Loader className="indent-sm-top indent-sm-bottom" />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default DesktopSearch;
